import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from 'react-query'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { getAllVaultMedia } from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useVaultCacheStore } from 'store/vaultCacheStore'

import style from './_add_to_popup.module.scss'
import vaultStyle from '../../_vault.module.scss'
import { VaultCardGrouped } from '../VaultCardGrouped'

interface AddToGroupPopUpProps {
  initialCategory: any
  selectedCategory: any
  selectedCategoryPostIds: string[]
  submitFn: (val: any, val2: any) => void
  cancelFn: () => void
  open: boolean
  loading?: boolean
  setQueryToInvalidate?: any
}

interface VaultMediaResponse {
  data: any[]
  nextPage: number | null
  existingIds: string[]
}

export const AddToGroupPopUp = ({
  initialCategory,
  selectedCategory,
  selectedCategoryPostIds,
  cancelFn,
  submitFn,
  open,
  loading,
  setQueryToInvalidate,
}: AddToGroupPopUpProps) => {
  const { t } = useTranslation()
  const modalData = useModalContext()

  const [selectedPosts, setSelecetedPosts] = useState<string[]>([])
  const [postIds, setPostIds] = useState<string[]>([])
  const [allSelectedMedia, setAllSelectedMedia] = useState<any>([])
  const [activeFilter, setActiveFilter] = useState('all')
  const [postsFilter, setPostsFilter] = useState('')
  const [postsSort, setPostsSort] = useState('')
  const [postsAsc, setPostsAsc] = useState('')
  const [media, setMedia] = useState<any>([])
  const [allMediaLength, setAllMediaLength] = useState(0)

  const userProfile = useUserProfile()
  const { getVaultMedia, setVaultMedia } = useVaultCacheStore()

  const cardsTypes = ['All', 'Feed', 'Premium', 'Chat', 'Story']

  const [activeCategory, setActiveCategory] = useState<any>(
    initialCategory || {
      name: 'All',
      id: null,
      selected: true,
      posts: 0,
      premium: 0,
      stories: 0,
      messages: 0,
    }
  )


  const normalizedCategoryId = useMemo<string | null>(() => {
    const id = activeCategory?.id

    if (!id || id === '0') return null

    return String(id)
  }, [activeCategory?.id])

  const feedFilter = {
    first_section: [
      {
        value: 'group_by_post',
        name: 'Group By Post',
        isToggle: true,
      },
    ],
    second_section: [
      {
        value: 'all_media',
        name: 'All Media',
      },
      {
        value: 'photos',
        name: 'Photos',
      },
      {
        value: 'videos',
        name: 'Videos',
      },
    ],
  }

  const feedSort = {
    first_section: [
      {
        value: 'most_recent',
        name: 'Latest Posts',
      },
      {
        value: 'most_purchased',
        name: 'Most Purchased',
      },
      {
        value: 'most_viewed',
        name: 'Most Viewed',
      },
      {
        value: 'most_liked',
        name: 'Most Liked',
      },
      {
        value: 'highest_tips',
        name: 'Highest Tips',
      },
      {
        value: 'most_comment',
        name: 'Most Commented',
      },
    ],
    second_section: [
      {
        value: 'asc',
        name: 'Ascending',
      },
      {
        value: 'desc',
        name: 'Descending',
      },
    ],
  }

  const messagesSort = {
    first_section: [
      {
        value: 'most_recent',
        name: 'Most Recent',
      },
      {
        value: 'most_purchased',
        name: 'Most Purchased',
      },
    ],
    second_section: [
      {
        value: 'asc',
        name: 'Ascending',
      },
      {
        value: 'desc',
        name: 'Descending',
      },
    ],
  }

  /**
   * FIX #1 (your requirement):
   * On modal open, ONLY hydrate from cache that matches:
   *   type=all&mapExistingPostId=false&show_duplicates=false&grouped=true (and offline=false)
   * i.e. do NOT let "opened from a category page" change which cache entry is used for initial hydration.
   *
   * So we create a canonical "type=all" cache key that ignores category.
   */
  const allTypeCacheParams = useMemo(
    () => ({
      grouped: true,
      mapExistingPostId: false,
      show_duplicates: false,
      offline: false,
      type: 'all',
    }),
    []
  )

  const allTypeCacheKey = useMemo(() => {
    return JSON.stringify([userProfile?.id ?? 'unknown', allTypeCacheParams])
  }, [userProfile?.id, allTypeCacheParams])

  const cachedAllFirstPage = getVaultMedia(allTypeCacheKey) as VaultMediaResponse | undefined

  /**
   * Actual request params for the current view state.
   *
   * NOTE:
   * - grouped is always present and always true in this modal (your requirement)
   * - category_id is supported now; HOWEVER:
   *   For initial "type=all" load behavior matching your URL, we intentionally DO NOT send category_id
   *   when activeFilter === 'all' so the request matches:
   *   /api/user/vault?page=1&type=all&mapExistingPostId=false&show_duplicates=false&grouped=true
   */
    const requestParams = useMemo(() => {
    return {
        filter: postsFilter || '',
        sort: postsSort || '',
        sortAsc: postsAsc || '',
        grouped: true,

        show_duplicates: false,
        mapExistingPostId: false,
        offline: false,

        //  always include, but null when "All"
        category: normalizedCategoryId,

        //  type/tab
        type: activeFilter,
    }
    }, [postsFilter, postsSort, postsAsc, normalizedCategoryId, activeFilter])

  const vaultCacheKey = useMemo(() => {
    return JSON.stringify([userProfile?.id ?? 'unknown', requestParams])
  }, [userProfile?.id, requestParams])

  const cachedFirstPageForCurrentKey = getVaultMedia(vaultCacheKey) as VaultMediaResponse | undefined

  useEffect(() => {
    setQueryToInvalidate([
      'vaultMedia',
      activeFilter,
      activeCategory,
      selectedCategoryPostIds,
      postsFilter,
      postsSort,
      postsAsc,
    ])
  }, [activeFilter, activeCategory, selectedCategoryPostIds, postsFilter, postsSort, postsAsc, setQueryToInvalidate])

  useEffect(() => {
    setAllSelectedMedia([])
    setSelecetedPosts([])
    setPostIds([])
    setAllMediaLength(0)
  }, [activeFilter, selectedCategory, postsAsc, postsFilter, postsSort])

  // Reset selections when modal opens
  const prevOpen = useRef(open)
  useEffect(() => {
    if (!prevOpen.current && open) {
      setAllSelectedMedia([])
      setSelecetedPosts([])
      setPostIds([])
      setAllMediaLength(0)
    }
    prevOpen.current = open
  }, [open])

  const queryKey = useMemo(() => {
    return ['vaultMedia:addToPopup', userProfile?.id ?? 'unknown', requestParams]
  }, [userProfile?.id, requestParams])

  /**
   * FIX #2:
   * Your service adds +1 internally (page: page + 1), so pageParam MUST start at 0.
   *
   * FIX #3:
   * initialData.pageParams MUST be [0], not [1], or react-query will think "page 1" is already loaded.
   *
   * FIX #4 (your requirement):
   * When activeFilter === 'all', hydrate ONLY from the canonical type=all cache entry (cachedAllFirstPage).
   * For other tabs, hydrate from the exact cache entry for that tab (cachedFirstPageForCurrentKey).
   */
  const initialFirstPage = activeFilter === 'all' ? cachedAllFirstPage : cachedFirstPageForCurrentKey

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<VaultMediaResponse>(
    queryKey,
    async ({ pageParam = 0 }) => {
      // If your getAllVaultMedia now forwards opts.type (or uses typeOrFilter), this will produce:
      // type=<activeFilter> in the URL params.
      return await getAllVaultMedia(activeFilter, pageParam, requestParams as any)
    },
    {
      enabled: open && !!userProfile?.id,
      getNextPageParam: lastPage => lastPage.nextPage ?? undefined,

      initialData: () =>
        initialFirstPage
          ? {
              pages: [initialFirstPage],
              pageParams: [0],
            }
          : undefined,

      refetchOnMount: 'always',
      staleTime: 0,

      onSuccess: resp => {
        const all = resp.pages.flatMap(p => p.data || [])
        setMedia(all)

        // Store under the current request key
        if (resp.pages?.[0]) setVaultMedia(vaultCacheKey, resp.pages[0])

        // Also store under canonical type=all key when we're on the all tab,
        // so subsequent opens can hydrate even if modal was opened from a category page.
        if (activeFilter === 'all' && resp.pages?.[0]) {
          setVaultMedia(allTypeCacheKey, resp.pages[0])
        }
      },
    }
  )

  /**
   * Keep media in sync:
   * - prefer live query result
   * - else prefer "initialFirstPage" (canonical all cache if on all tab)
   */
  useEffect(() => {
    if (data) {
      const all = data.pages.flatMap(p => p.data || [])
      setMedia(all)
      return
    }

    if (initialFirstPage?.data?.length) {
      setMedia(initialFirstPage.data)
      return
    }

    setMedia([])
  }, [data, initialFirstPage])

  /**
   * FIX #5:
   * Prevent "page 2 on load" from IntersectionObserver firing immediately.
   * Common in modals when the sentinel is visible right away.
   */
  const didLoadFirstPageRef = useRef(false)
  useEffect(() => {
    if (!open) {
      didLoadFirstPageRef.current = false
      return
    }
    if (data?.pages?.length ?? initialFirstPage?.data?.length) {
      didLoadFirstPageRef.current = true
    }
  }, [open, data?.pages?.length, initialFirstPage?.data?.length])

  const observer = useRef<IntersectionObserver>()

  const lastPostRef = useCallback(
    (node: any) => {
      if (!didLoadFirstPageRef.current) return
      if (isLoading) return
      if (isFetchingNextPage) return
      if (!hasNextPage) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '150px' }
      )

      if (node) observer.current.observe(node)
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]
  )

  const toggleSelectedPosts = (val: any) => {
    let calcNumber = 0
    if (val.post) {
      if (val.post.videos.length > 0 && val.post.photos.length > 0) {
        calcNumber = val.post.videos.length + val.post.photos.length
      } else if (val.post.videos.length > 0) {
        calcNumber = val.post.videos.length
      } else if (val.post.photos.length > 0) {
        calcNumber = val.post.photos.length
      }
    }
    if (val.message?.message) {
      if (val.message.message.videos.length > 0 && val.message.message.photos.length > 0) {
        calcNumber = val.message.message.videos.length + val.message.message.photos.length
      } else if (val.message.message.videos.length > 0) {
        calcNumber = val.message.message.videos.length
      } else if (val.message.message.photos.length > 0) {
        calcNumber = val.message.message.photos.length
      }
    }
    if (val.story) {
      calcNumber = 1
    }

    if (val.id) {
      if (selectedPosts.includes(val.id)) {
        const newArray = selectedPosts.filter(
          (item: string) => item !== val.id
        )
        setSelecetedPosts(newArray)
      } else {
        if (allMediaLength + calcNumber <= 40) {
          setSelecetedPosts((prevState: string[]) => [
            ...prevState,
            val.id
          ])
        }
      }

      if (postIds.includes(val.id)) {
        const newArray = postIds.filter(
          (item: string) => item !== val.id
        )
        setPostIds(newArray)
      } else {
        if (allMediaLength + calcNumber <= 40) {
          setPostIds((prevState: string[]) => [
            ...prevState,
            val.id
          ])
        }
      }
    }

    if (allSelectedMedia.some((item: any) => item.id === val.id)) {
      const newArray = allSelectedMedia.filter((item: any) => item.id !== val.id)
      setAllSelectedMedia(newArray)
    } else {
      if (allMediaLength + calcNumber <= 40) {
        setAllSelectedMedia((prevState: any) => [...prevState, val])
      } else {
        addToast('error', 'Only 40 media files can be selected at once')
      }
    }
  }

  return (
    <div className={`${style.container} ${open ? style.container_open : ''}`}>
      <div className={style.title}>
        <div className='header__linkback' onClick={cancelFn}></div>
        {t('sharedMedia')}
      </div>

      <div className={style.header}>
        <LayoutHeader
          type='title-with-buttons'
          buttons={
            activeFilter === 'feed' || activeFilter === 'premium' || activeFilter === 'messages'
              ? [
                  {
                    type: 'sort',
                    elements: activeFilter === 'feed' || activeFilter === 'premium' ? feedSort : messagesSort,
                    sortingProps: {
                      selectedSort: postsSort,
                      selectedOrder: postsAsc,
                    },
                    applyFn: (val1: string, val2: string) => {
                      if (val1.trim() === '' || val2.trim() === '') {
                        setPostsSort('')
                        setPostsAsc('')
                        return
                      }
                      setPostsSort(val1)
                      setPostsAsc(val2)
                    },
                  },
                  {
                    type: 'filter',
                    elements: feedFilter,
                    sortingProps: {
                      selectedSort: 'group_by_post',
                      selectedOrder: postsFilter,
                    },
                    applyFn: (val1: string | boolean, val2: string) => {
                      // grouped is always true in this modal, so ignore val1
                      setPostsFilter(val2)
                    },
                  },
                ]
              : [
                  {
                    type: 'filter',
                    elements: feedFilter,
                    sortingProps: {
                      selectedSort: 'group_by_post',
                      selectedOrder: postsFilter,
                    },
                    applyFn: (val1: string | boolean, val2: string) => {
                      // grouped is always true in this modal, so ignore val1
                      setPostsFilter(val2)
                    },
                  },
                ]
          }
          titleWithIcon={
            <div
              className={vaultStyle.select_category_container}
              onClick={() => {
                modalData.addModal(
                  'Select Category',
                  <SelectCategoriesModal
                    onlyOne
                    selectedCategories={[activeCategory]}
                    onSave={val => {
                      if (val.length > 0) {
                        setActiveCategory(val[0])
                      } else {
                        setActiveCategory({
                          name: 'All',
                          id: null,
                          selected: true,
                          posts: 0,
                          premium: 0,
                          stories: 0,
                          messages: 0,
                        })
                      }
                      modalData.clearModal()
                    }}
                    onCancel={modalData.clearModal}
                  />
                )
              }}
            >
              <div className={vaultStyle.select_category_icon}>
                <spriteIcons.IconList color='#ffffff' width='14' height='14' />
              </div>
              <div className={vaultStyle.select_category_text}>{t(activeCategory?.name)}</div>
            </div>
          }
        />
      </div>

{/*       <div className={style.separator}></div>
 */}
      <div className={style.cards_types_container}>
        {cardsTypes.map((cardType: string, index: number) => (
          <div
            className={`${style.card_type} ${activeFilter === cardType.toLowerCase() ? style.card_type_selected : ''}`}
            key={index}
            onClick={() => setActiveFilter(cardType.toLowerCase())}
          >
            {cardType}
          </div>
        ))}
      </div>

      <div className={style.cards_container}>
        {!isLoading &&
          media.length > 0 &&
          media
            .filter((item: any) => (item.story ? item.story.video_src || item.story.image_src : item))
            .map((m: any, index: number) => (
              <VaultCardGrouped
                key={index}
                onCirlceClick={toggleSelectedPosts}
                media={m}
                selected={allSelectedMedia.some((item: any) => item.id === m.id)}
                selectedText={selectedPosts.indexOf(m.id) + 1}
                grouped={true}
              />
            ))}

        {(hasNextPage ?? isLoading) && (
          <div style={{ width: '100%' }} ref={lastPostRef}>
            <div className='loader'></div>
          </div>
        )}
      </div>

      <div className={style.add_to_buttons}>
        <Button
          text={'Cancel'}
          color='grey'
          customClass={`${style.add_to_button} ${loading ? style.button_loading : ''}`}
          clickFn={cancelFn}
        />
        <Button
          text={'Add'}
          color='black'
          customClass={`${style.add_to_button} ${loading ? style.button_loading : ''}`}
          clickFn={() => submitFn(allSelectedMedia, selectedPosts)}
        />
      </div>
    </div>
  )
}